import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Database, FileX, Loader2, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PASSWORD_INPUT_PROPS } from "@/utils/passwordInputProps";

const COLLECTION_LABELS = {
    users: "Users",
    projects: "Projects",
    tasks: "Tasks",
    appointments: "Appointments",
    calendar_appointments: "Calendar entries",
    journeys: "Customer journeys",
    documents: "Documents",
    messages: "Messages",
    notifications: "Notifications",
    notification_history: "Notification history",
};

/**
 * `/operations/administration` → Data Management tab.
 *
 * Wipe-all-demo-data control. Runs against every document flagged
 * `is_test_data=true` and cascades through the standard user purge so
 * seeded demo data (marker: `is_seeded_demo=true`) is never removed.
 * The confirm modal requires the literal word `DELETE` to be typed
 * before the destructive action fires.
 */
export default function DataManagementTab() {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openConfirm, setOpenConfirm] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setPreview(await api.adminWipeDemoDataPreview()); }
        catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load preview");
            setPreview(null);
        }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totalRows = preview
        ? Object.entries(preview)
              .filter(([k]) => !k.startsWith("_"))
              .reduce((sum, [, v]) => sum + (v || 0), 0)
        : 0;
    const nothingToWipe = !loading && totalRows === 0;

    return (
        <div className="space-y-4" data-testid="ops-admin-data-management">
            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 md:p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#E1E6F0] text-[#131B2F] flex items-center justify-center shrink-0">
                        <Database size={18} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#131B2F]">What "Wipe All Demo Data" removes</div>
                        <p className="mt-1 text-xs text-[#4B5565] leading-relaxed">
                            Every document flagged <span className="font-mono text-[11px] bg-[#F5F5F7] px-1 py-0.5 rounded">is_test_data:&nbsp;true</span> — the transient records that testing agents and QA runs create during a build cycle.
                            The three seeded demo accounts (<span className="font-mono text-[11px] bg-[#F5F5F7] px-1 py-0.5 rounded">is_seeded_demo:&nbsp;true</span>) and their bundled project are always preserved, so the tenant is never blank after the wipe.
                        </p>
                    </div>
                </div>

                <div className="mt-5 rounded-xl border border-[#F5F5F7] bg-[#FAFAFC] p-4">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#4B5565] font-medium">Impact preview</div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ops-admin-wipe-preview">
                        {loading ? (
                            <div className="col-span-full text-xs text-[#4B5565] py-4 text-center">
                                <Loader2 size={12} className="inline animate-spin" /> Loading preview…
                            </div>
                        ) : preview ? (
                            Object.entries(preview)
                                .filter(([k]) => !k.startsWith("_"))
                                .filter(([, v]) => v > 0)
                                .map(([k, v]) => (
                                    <div key={k} className="text-center" data-testid={`ops-admin-wipe-count-${k}`}>
                                        <div className="text-2xl font-semibold text-[#131B2F]">{v}</div>
                                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#4B5565]">
                                            {COLLECTION_LABELS[k] || k}
                                        </div>
                                    </div>
                                ))
                        ) : null}
                        {!loading && preview && totalRows === 0 && (
                            <div className="col-span-full py-4 text-center text-xs text-[#4B5565]" data-testid="ops-admin-wipe-empty">
                                No test data currently in the system — nothing to wipe.
                            </div>
                        )}
                    </div>
                    {preview && (
                        <div className="mt-3 text-[11px] text-[#4B5565]">
                            <span className="text-[#2E7E22] font-medium">Protected:</span> {preview._seeded_demo_users_protected || 0} seeded demo user{preview._seeded_demo_users_protected === 1 ? "" : "s"} + their persistent records.
                        </div>
                    )}
                </div>

                <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                    <div className="text-[11px] text-[#4B5565]">
                        Requires typing <span className="font-mono text-[#DA3535] font-semibold">DELETE</span> in the confirmation dialog. This action is written to the security audit trail.
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpenConfirm(true)}
                        disabled={nothingToWipe}
                        data-testid="ops-admin-wipe-demo-data-btn"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#DA3535] hover:bg-[#B91C1C] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Trash2 size={14} strokeWidth={1.75} />
                        Wipe All Demo Data
                    </button>
                </div>
            </div>

            <WipeConfirmModal
                open={openConfirm}
                preview={preview}
                onCancel={() => setOpenConfirm(false)}
                onDone={async (result) => {
                    setOpenConfirm(false);
                    toast.success(`Wiped ${result.users_purged} test user${result.users_purged === 1 ? "" : "s"} and ${sumDeleted(result.deleted_counts)} record${sumDeleted(result.deleted_counts) === 1 ? "" : "s"}.`);
                    await load();
                }}
            />

            {/* ADMIN-DATA-ORPHAN-CLEANUP · Orphaned Attention Exceptions card */}
            <OrphanExceptionsCard />

            {/* OPS-008A · Document Reference Audit card (read-only) */}
            <DocumentReferenceAuditCard />

            {/* DEV-025 · Orphaned zero-byte document cleanup */}
            <DocumentOrphanCleanupCard />
        </div>
    );
}


/**
 * DEV-025 · Orphaned zero-byte document cleanup.
 *
 * Scans every stored document and shows the exact count matching
 * the strict predicate before allowing any removal:
 *
 *   1. Zero-byte / no uploaded file, AND
 *   2. No valid project association, AND
 *   3. No valid customer association.
 *
 * Removal is a hard delete (approved for this DEV-025 tranche); the
 * apply endpoint refuses unless the caller echoes the fresh
 * dry-run's `content_hash` AND the literal "REMOVE" confirmation,
 * and re-validates every id against the same predicate at apply
 * time. Never surfaces a broad "delete all" affordance.
 */
function DocumentOrphanCleanupCard() {
    const [scan, setScan] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);

    const runScan = useCallback(async () => {
        setScanning(true);
        setScanError(null);
        try {
            const result = await api.adminDocumentOrphansDryRun();
            setScan(result);
            setSelected(new Set());
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Scan failed";
            setScan(null);
            setScanError({ message, status: e?.response?.status });
            toast.error(message);
        } finally {
            setScanning(false);
        }
    }, []);

    const rows = scan?.orphaned || [];
    const orphanTotal = scan?.summary?.orphaned_total ?? rows.length;
    const documentsScanned = scan?.summary?.documents_scanned ?? 0;
    const allSelected = rows.length > 0 && selected.size === rows.length;

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
    };

    return (
        <div
            className="rounded-2xl border border-[#E5E7EB] bg-white p-5 md:p-6"
            data-testid="ops-admin-doc-orphan-cleanup"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#FDECEC] text-[#B91C1C] flex items-center justify-center shrink-0">
                    <FileX size={18} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#131B2F]">Orphaned duplicate documents</div>
                    <p className="mt-1 text-xs text-[#4B5565] leading-relaxed">
                        Removes zero-byte document records that have <span className="font-semibold">no uploaded file</span> AND <span className="font-semibold">no valid project or customer association</span>. Documents with a real file (any positive file size), a linked customer, or a linked project are always preserved — even when another document shares the same title, category or upload date.
                    </p>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-[11px] text-[#4B5565]" data-testid="ops-admin-doc-orphan-summary">
                    {scan ? (
                        orphanTotal === 0
                            ? <span data-testid="ops-admin-doc-orphan-empty">No orphaned zero-byte documents found across {documentsScanned} scanned.</span>
                            : (
                                <>
                                    <span data-testid="ops-admin-doc-orphan-count">{orphanTotal}</span>
                                    {" "}orphaned document{orphanTotal === 1 ? "" : "s"} match the strict predicate across {documentsScanned} scanned.
                                </>
                            )
                    ) : (
                        <span>Run a scan to classify every document against the orphan predicate.</span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={runScan}
                    disabled={scanning}
                    data-testid="ops-admin-doc-orphan-scan-btn"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#131B2F]/60 bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-50"
                >
                    {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} strokeWidth={1.75} />}
                    Scan for orphaned documents
                </button>
            </div>

            {scanError && (
                <div className="mt-3 rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                     data-testid="ops-admin-doc-orphan-scan-error">
                    {scanError.status === 403 ? "You do not have permission to run this cleanup." : scanError.message}
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-4 overflow-x-auto border border-[#E5E7EB] rounded-md">
                    <table className="w-full text-xs" data-testid="ops-admin-doc-orphan-table">
                        <thead className="bg-[#F5F5F7] text-[#4B5565] uppercase tracking-[0.1em] text-[10px]">
                            <tr>
                                <th className="p-2 text-left w-8">
                                    <input
                                        type="checkbox"
                                        aria-label="Select all orphaned documents"
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        data-testid="ops-admin-doc-orphan-select-all"
                                    />
                                </th>
                                <th className="p-2 text-left">Title</th>
                                <th className="p-2 text-left">Category</th>
                                <th className="p-2 text-left">Uploaded</th>
                                <th className="p-2 text-left">Stable ID</th>
                                <th className="p-2 text-left">Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const short = (r.id || "").slice(0, 8);
                                return (
                                    <tr key={r.id} data-testid={`ops-admin-doc-orphan-row-${r.id}`}
                                        className="border-t border-[#E5E7EB]">
                                        <td className="p-2 align-top">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${r.title || r.file_name || r.id}`}
                                                checked={selected.has(r.id)}
                                                onChange={() => toggle(r.id)}
                                                data-testid={`ops-admin-doc-orphan-select-${r.id}`}
                                            />
                                        </td>
                                        <td className="p-2 align-top">{r.title || r.file_name || "Untitled document"}</td>
                                        <td className="p-2 align-top">{r.category || "—"}</td>
                                        <td className="p-2 align-top">{(r.uploaded_at || "").slice(0, 10) || "—"}</td>
                                        <td className="p-2 align-top font-mono" title={r.id || ""}>{short}…</td>
                                        <td className="p-2 align-top">{r.file_size ? `${r.file_size} B` : "0 B"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-3 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => setConfirmOpen(true)}
                        disabled={selected.size === 0}
                        data-testid="ops-admin-doc-orphan-remove-btn"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#DA3535] text-white text-sm font-medium hover:bg-[#B91C1C] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Trash2 size={14} strokeWidth={1.75} />
                        Remove selected ({selected.size})
                    </button>
                </div>
            )}

            <DocumentOrphanRemoveModal
                open={confirmOpen}
                selectedCount={selected.size}
                onCancel={() => setConfirmOpen(false)}
                onDone={async (result) => {
                    setConfirmOpen(false);
                    toast.success(`Removed ${result.removed_count} orphaned document${result.removed_count === 1 ? "" : "s"}.`);
                    await runScan();
                }}
                onSubmit={async () => {
                    return api.adminDocumentOrphansApply({
                        document_ids: [...selected],
                        content_hash: scan?.content_hash,
                        confirmation: "REMOVE",
                    });
                }}
            />
        </div>
    );
}


function DocumentOrphanRemoveModal({ open, selectedCount, onCancel, onSubmit, onDone }) {
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setConfirmText("");
        setError(null);
        const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel, busy]);

    if (!open) return null;

    const canConfirm = confirmText === "REMOVE" && !busy && selectedCount > 0;

    async function submit() {
        if (!canConfirm) return;
        setBusy(true);
        setError(null);
        try {
            const r = await onSubmit();
            onDone(r);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = (typeof detail === "object" ? detail?.error_code : null) || null;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Removal failed";
            const humanised = code === "content_hash_stale"
                ? "The scan is out of date. Close this dialog, re-scan, then try again."
              : code === "predicate_no_longer_matches"
                ? "At least one selected document no longer matches the orphan predicate. Close this dialog and re-scan."
              : code === "confirmation_required"
                ? "Confirmation text was not recognised."
              : e?.response?.status === 403
                ? "You do not have permission to remove orphaned documents."
              : message;
            setError({ code, message: humanised });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid="ops-admin-doc-orphan-confirm"
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2F]">
                        <AlertTriangle size={16} className="text-[#DA3535]" />
                        Remove orphaned documents
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4 text-sm text-[#4B5565]">
                    <p>
                        This will <span className="font-semibold text-[#DA3535]">permanently delete</span> <span className="font-mono text-[#131B2F]" data-testid="ops-admin-doc-orphan-confirm-count">{selectedCount}</span> document record{selectedCount === 1 ? "" : "s"} that have no uploaded file bytes AND no valid project or customer association.
                    </p>
                    <p className="text-[11px]">
                        Documents with a real uploaded file, a linked project or a linked customer are <span className="font-semibold text-[#2E7E22]">never</span> touched — even when another record shares the same title or category. The removal is not recoverable.
                    </p>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">
                            Type <span className="font-mono text-[#DA3535] font-semibold">REMOVE</span> to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="REMOVE"
                            data-testid="ops-admin-doc-orphan-confirm-input"
                            {...PASSWORD_INPUT_PROPS}
                            autoComplete="off"
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 font-mono text-base outline-none focus:border-[#DA3535] focus:ring-1 focus:ring-[#DA3535]/30"
                        />
                    </div>
                    {error && (
                        <div className="rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                             data-testid="ops-admin-doc-orphan-confirm-error"
                             data-error-code={error.code || undefined}>
                            {error.message}
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="ops-admin-doc-orphan-confirm-cancel"
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canConfirm}
                        data-testid="ops-admin-doc-orphan-confirm-ok"
                        className="h-10 px-4 rounded-md bg-[#DA3535] hover:bg-[#B91C1C] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Remove selected
                    </button>
                </div>
            </div>
        </div>
    );
}


/**
 * OPS-008A · Read-only document-reference audit summary.
 *
 * Scans every document, classifies each row's reference integrity
 * (project + customer + file presence) and surfaces counts by
 * classification. Never mutates. The card ONLY reports — the
 * dispatch path documented in the audit output (per-document
 * classification + reasons) is handled through the existing
 * Documents workspace.
 */
function DocumentReferenceAuditCard() {
    const [scan, setScan] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState(null);

    const runScan = useCallback(async () => {
        setScanning(true);
        setScanError(null);
        try {
            const result = await api.adminDocumentReferencesDryRun();
            setScan(result);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Scan failed";
            setScan(null);
            setScanError({ message, status: e?.response?.status });
        } finally {
            setScanning(false);
        }
    }, []);

    const summary = scan?.summary || null;
    const needsReview = summary?.needs_review_total || 0;
    const byClass = summary?.by_classification || {};

    return (
        <div
            className="rounded-2xl border border-[#E5E7EB] bg-white p-5 md:p-6"
            data-testid="ops-admin-doc-refs-audit"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#EEF2FF] text-[#1E3A8A] flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#131B2F]">Document reference audit</div>
                    <p className="mt-1 text-xs text-[#4B5565] leading-relaxed">
                        Read-only scan of every stored document. Classifies each row by whether its project + customer still exist and whether real file bytes are present. It <span className="font-semibold">does not delete, relink or archive</span> any document; genuine files are always preserved. Use the Documents workspace <span className="font-mono text-[11px] bg-[#F5F5F7] px-1 py-0.5 rounded">Needs review</span> filter to inspect and action affected rows individually.
                    </p>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-[11px] text-[#4B5565]" data-testid="ops-admin-doc-refs-summary">
                    {scan ? (
                        needsReview === 0
                            ? <span data-testid="ops-admin-doc-refs-empty">Every document has a healthy project + customer link.</span>
                            : `${needsReview} document${needsReview === 1 ? "" : "s"} require review across ${summary.documents_total} total.`
                    ) : (
                        <span>Run a scan to classify every document's reference integrity.</span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={runScan}
                    disabled={scanning}
                    data-testid="ops-admin-doc-refs-scan-btn"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#131B2F]/60 bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-50"
                >
                    {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} strokeWidth={1.75} />}
                    Scan document references
                </button>
            </div>

            {scanError && (
                <div className="mt-3 rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                     data-testid="ops-admin-doc-refs-scan-error">
                    {scanError.status === 403 ? "You do not have permission to run this audit." : scanError.message}
                </div>
            )}

            {scan && needsReview > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="ops-admin-doc-refs-breakdown">
                    {["HUMAN_REVIEW", "ARCHIVE_CANDIDATE", "LEGACY_UNLINKED", "FILE_MISSING", "RELINK_SAFE"].map((k) => (
                        (byClass[k] || 0) > 0 && (
                            <div key={k} className="text-center rounded-md border border-[#E5E7EB] bg-[#FAFAFC] p-3" data-testid={`ops-admin-doc-refs-count-${k}`}>
                                <div className="text-2xl font-semibold text-[#131B2F]">{byClass[k]}</div>
                                <div className="text-[10px] uppercase tracking-[0.1em] text-[#4B5565]">
                                    {k.replace(/_/g, " ").toLowerCase()}
                                </div>
                            </div>
                        )
                    ))}
                </div>
            )}

            {scan && (
                <div className="mt-3 text-[11px] text-[#4B5565]" data-testid="ops-admin-doc-refs-hash">
                    Scan hash: <code className="font-mono">{scan.content_hash}</code>
                </div>
            )}
        </div>
    );
}


/**
 * Resolves stale Attention records after their underlying entities
 * (project / task / appointment / survey / resource) have been
 * deleted. The card is strictly a metadata cleanup — it never
 * restores or deletes business data.
 */
function OrphanExceptionsCard() {
    const [scan, setScan] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);

    const runScan = useCallback(async () => {
        setScanning(true);
        setScanError(null);
        try {
            const result = await api.adminOrphanExceptionsDryRun();
            setScan(result);
            setSelected(new Set());
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Scan failed";
            setScan(null);
            setScanError({ message, status: e?.response?.status });
            toast.error(message);
        } finally {
            setScanning(false);
        }
    }, []);

    const rows = scan?.orphaned || [];
    const allEligible = rows.length > 0 && selected.size === rows.length;

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected(allEligible ? new Set() : new Set(rows.map((r) => r.id)));
    };

    return (
        <div
            className="rounded-2xl border border-[#E5E7EB] bg-white p-5 md:p-6"
            data-testid="ops-admin-orphan-exceptions"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#FEF3C7] text-[#92400E] flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#131B2F]">Orphaned Attention Exceptions</div>
                    <p className="mt-1 text-xs text-[#4B5565] leading-relaxed">
                        Resolves Attention Centre records whose underlying project, task, appointment, survey or resource has been deleted.
                        It <span className="font-semibold">does not restore or delete</span> business data — only the stale Attention record is closed and the audit trail is preserved.
                    </p>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-[11px] text-[#4B5565]" data-testid="ops-admin-orphan-summary">
                    {scan ? (
                        rows.length === 0
                            ? <span data-testid="ops-admin-orphan-empty">No orphaned Attention exceptions found.</span>
                            : `${rows.length} orphaned exception${rows.length === 1 ? "" : "s"} found. Select the ones to resolve.`
                    ) : (
                        <span>Run a scan to check for stale Attention records.</span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={runScan}
                    disabled={scanning}
                    data-testid="ops-admin-orphan-scan-btn"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#131B2F]/60 bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-50"
                >
                    {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} strokeWidth={1.75} />}
                    Scan for orphaned exceptions
                </button>
            </div>

            {scanError && (
                <div className="mt-3 rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                     data-testid="ops-admin-orphan-scan-error">
                    {scanError.status === 403 ? "You do not have permission to run this cleanup." : scanError.message}
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-4 overflow-x-auto border border-[#E5E7EB] rounded-md">
                    <table className="w-full text-xs" data-testid="ops-admin-orphan-table">
                        <thead className="bg-[#F5F5F7] text-[#4B5565] uppercase tracking-[0.1em] text-[10px]">
                            <tr>
                                <th className="p-2 text-left w-8">
                                    <input
                                        type="checkbox"
                                        aria-label="Select all eligible"
                                        checked={allEligible}
                                        onChange={toggleAll}
                                        data-testid="ops-admin-orphan-select-all"
                                    />
                                </th>
                                <th className="p-2 text-left">Customer</th>
                                <th className="p-2 text-left">Rule</th>
                                <th className="p-2 text-left">Entity</th>
                                <th className="p-2 text-left">Stable ID</th>
                                <th className="p-2 text-left">First seen</th>
                                <th className="p-2 text-left">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const short = (r.affected_entity_id || r.project_id || r.id || "").slice(0, 8);
                                return (
                                    <tr key={r.id} data-testid={`ops-admin-orphan-row-${r.id}`}
                                        className="border-t border-[#E5E7EB]">
                                        <td className="p-2 align-top">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${r.rule_key}`}
                                                checked={selected.has(r.id)}
                                                onChange={() => toggle(r.id)}
                                                data-testid={`ops-admin-orphan-select-${r.id}`}
                                            />
                                        </td>
                                        <td className="p-2 align-top">{r.customer_display_name || "—"}</td>
                                        <td className="p-2 align-top font-mono">{r.rule_key}</td>
                                        <td className="p-2 align-top">{r.affected_entity_kind}</td>
                                        <td className="p-2 align-top font-mono" title={r.affected_entity_id || r.project_id || ""}>
                                            {short}…
                                        </td>
                                        <td className="p-2 align-top">{(r.first_seen_at || "").slice(0, 10)}</td>
                                        <td className="p-2 align-top">{r.orphan_reason}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-3 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => setConfirmOpen(true)}
                        disabled={selected.size === 0}
                        data-testid="ops-admin-orphan-resolve-btn"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#131B2F] text-white text-sm font-medium hover:bg-[#0F1526] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ShieldCheck size={14} strokeWidth={1.75} />
                        Resolve selected ({selected.size})
                    </button>
                </div>
            )}

            <OrphanResolveModal
                open={confirmOpen}
                selectedCount={selected.size}
                onCancel={() => setConfirmOpen(false)}
                onDone={async (result) => {
                    setConfirmOpen(false);
                    toast.success(`Resolved ${result.resolved_count} orphaned exception${result.resolved_count === 1 ? "" : "s"}.`);
                    // Refresh scan and global Attention count exactly once.
                    await runScan();
                    try { window.dispatchEvent(new Event("solarflow:attention:refresh")); } catch {}
                }}
                onSubmit={async () => {
                    return api.adminOrphanExceptionsResolve({
                        exception_ids: [...selected],
                        content_hash: scan?.content_hash,
                        confirmation: "RESOLVE",
                    });
                }}
            />
        </div>
    );
}


function OrphanResolveModal({ open, selectedCount, onCancel, onSubmit, onDone }) {
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setConfirmText("");
        setError(null);
        const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel, busy]);

    if (!open) return null;

    const canConfirm = confirmText === "RESOLVE" && !busy && selectedCount > 0;

    async function submit() {
        if (!canConfirm) return;
        setBusy(true);
        setError(null);
        try {
            const r = await onSubmit();
            onDone(r);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = (typeof detail === "object" ? detail?.error_code : null) || null;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Resolve failed";
            const humanised = code === "content_hash_stale"
                ? "The scan is out of date. Close this dialog, re-scan, then try again."
              : code === "entity_restored"
                ? "One of the selected exceptions' entities has come back. Close this dialog and re-scan."
              : code === "confirmation_required"
                ? "Confirmation text was not recognised."
              : e?.response?.status === 403
                ? "You do not have permission to resolve orphaned exceptions."
              : message;
            setError({ code, message: humanised });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid="ops-admin-orphan-confirm"
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2F]">
                        <AlertTriangle size={16} className="text-[#92400E]" />
                        Resolve orphaned Attention exceptions
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4 text-sm text-[#4B5565]">
                    <p>
                        This will close <span className="font-mono text-[#131B2F]" data-testid="ops-admin-orphan-confirm-count">{selectedCount}</span> stale Attention exception{selectedCount === 1 ? "" : "s"} whose underlying entity has been deleted.
                        Business data (projects, customers, tasks, appointments, surveys, resources, notes, files, historical audit) is <span className="font-semibold">not modified</span>.
                    </p>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">
                            Type <span className="font-mono text-[#131B2F] font-semibold">RESOLVE</span> to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="RESOLVE"
                            data-testid="ops-admin-orphan-confirm-input"
                            {...PASSWORD_INPUT_PROPS}
                            autoComplete="off"
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 font-mono text-base outline-none focus:border-[#131B2F] focus:ring-1 focus:ring-[#131B2F]/30"
                        />
                    </div>
                    {error && (
                        <div className="rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                             data-testid="ops-admin-orphan-confirm-error"
                             data-error-code={error.code || undefined}>
                            {error.message}
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="ops-admin-orphan-confirm-cancel"
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canConfirm}
                        data-testid="ops-admin-orphan-confirm-ok"
                        className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0F1526] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Resolve selected
                    </button>
                </div>
            </div>
        </div>
    );
}

function sumDeleted(counts = {}) {
    return Object.entries(counts)
        .filter(([k]) => k !== "users")
        .reduce((s, [, v]) => s + (v || 0), 0);
}


function WipeConfirmModal({ open, preview, onCancel, onDone }) {
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setConfirmText("");
        const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel, busy]);

    if (!open) return null;

    const canConfirm = confirmText === "DELETE" && !busy;

    async function submit() {
        if (!canConfirm) return;
        setBusy(true);
        try { const r = await api.adminWipeDemoData("DELETE"); onDone(r); }
        catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Wipe failed");
        } finally { setBusy(false); }
    }

    const rows = preview
        ? Object.entries(preview)
              .filter(([k, v]) => !k.startsWith("_") && v > 0)
        : [];

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid="ops-admin-wipe-confirm"
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2F]">
                        <AlertTriangle size={16} className="text-[#DA3535]" />
                        Wipe All Demo Data
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4 text-sm text-[#4B5565]">
                    <p>
                        This will permanently delete every record flagged as <span className="font-mono text-[#131B2F]">is_test_data</span> across:
                    </p>
                    <ul className="text-[11px] grid grid-cols-2 gap-y-1 gap-x-4">
                        {rows.length === 0
                            ? <li className="col-span-2 text-[#4B5565]">Nothing currently flagged.</li>
                            : rows.map(([k, v]) => (
                                <li key={k} className="flex items-center justify-between">
                                    <span>{COLLECTION_LABELS[k] || k}</span>
                                    <span className="font-mono text-[#DA3535] font-semibold">{v}</span>
                                </li>
                            ))
                        }
                    </ul>
                    <p className="text-[11px] text-[#2E7E22]">
                        Seeded demo accounts and their persistent data will be preserved.
                    </p>

                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">
                            Type <span className="font-mono text-[#DA3535] font-semibold">DELETE</span> to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="DELETE"
                            data-testid="ops-admin-wipe-confirm-input"
                            {...PASSWORD_INPUT_PROPS}
                            autoComplete="off"
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 font-mono text-base outline-none focus:border-[#DA3535] focus:ring-1 focus:ring-[#DA3535]/30"
                        />
                    </div>
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="ops-admin-wipe-confirm-cancel"
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canConfirm}
                        data-testid="ops-admin-wipe-confirm-ok"
                        className="h-10 px-4 rounded-md bg-[#DA3535] hover:bg-[#B91C1C] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Wipe All Demo Data
                    </button>
                </div>
            </div>
        </div>
    );
}
